import { API_BASE_URL, ApiError } from './client';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';

/**
 * A unit's own word for a kind of event. The list is the unit's, not the platform's — two units
 * running the same programme will not have the same words for it, and that is the point.
 */
/**
 * What a kind suggests for a new event of that kind.
 *
 * The two times are **wall-clock strings** — `19:00`, no date and no zone — because that is what
 * they are. A reading off a clock becomes an instant only against a date, and the date is the one
 * the leader is about to pick; an instant here would carry whatever zone rendered it and make
 * "Mondays at 19:00" drift an hour every March.
 */
export interface EventTypeDefaults {
  durationMinutes: number | null;
  startTimeOfDay: string | null;
  endTimeOfDay: string | null;
  locationName: string | null;
  locationAddress: string | null;
  description: string | null;
  /**
   * Which coordination questions events of this kind ask. Null is a real third state -- leave it
   * to the kind this one refines, and then to the product default of on -- so the management
   * screen offers three choices rather than a checkbox.
   */
  requiresRsvp: boolean | null;
  requiresForms: boolean | null;
  requiresPacking: boolean | null;
  requiresTravel: boolean | null;
  requiresProgram: boolean | null;
}

export interface EventType {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  /** The type this one refines, or null when it is a root. Pickers group children under it. */
  parentEventTypeId: string | null;
  /**
   * Already resolved through the parent, so the form holds answers rather than the inheritance
   * rule. These prefill boxes and are never read back through a saved event.
   */
  defaults: EventTypeDefaults;
  /**
   * Which of the eight swatches this kind reads in, already resolved through the parent. Never
   * null — a kind with no colour of its own renders the one it refines, and failing that the
   * neutral. A key, never a hex: see `brand/eventColors`.
   */
  colorKey: string;
  /**
   * How much this kind matters when the weekly digest has to choose between them, already resolved
   * through the parent. Never null — a kind that says nothing reads as `Standard`.
   */
  digestPriority: DigestPriority;
}

/**
 * How much a *kind* of event matters to the weekly digest.
 *
 * It lives on the type and never on the event: how much a kind of thing matters is not a
 * per-occurrence decision, and a control on one event would change what every family in the unit is
 * mailed. Three values rather than a number, because a rank invites a fourth and an evening spent
 * ordering event types against each other.
 */
export type DigestPriority = 'Major' | 'Standard' | 'Routine';

/** The management screen's view: the picker's fields plus what a leader needs before changing one. */
export interface EventTypeAdmin extends EventType {
  /**
   * What this type sets **itself**, not what it inherits. The screen edits a type, and a parent's
   * value shown in its box becomes an unasked-for copy the moment anybody saves.
   */
  ownDefaults: EventTypeDefaults;
  /**
   * What this type sets **itself**, or null when it takes its parent's. The editor's box, which is
   * allowed to be empty in a way the calendar's never is.
   */
  ownColorKey: string | null;
  /**
   * What this type sets **itself**, or null when it takes its parent's. The editor's box, which is
   * allowed to be empty in the way the resolved answer beside it never is.
   */
  ownDigestPriority: DigestPriority | null;
  isArchived: boolean;
  /** How many of this unit's events carry it, so a rename's reach is visible before making it. */
  eventCount: number;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const eventTypesApi = {
  /** Live types only. An archived one is not something to pick; events already filed under it keep it. */
  list: (unitId: string) => call<EventType[]>(`/units/${unitId}/event-types/`),

  /** Live and archived, with usage counts. Officer and above. */
  listAll: (unitId: string) => call<EventTypeAdmin[]>(`/units/${unitId}/event-types/all`),

  create: (
    unitId: string,
    input: {
      name: string;
      description?: string | null;
      parentEventTypeId?: string | null;
      defaults?: Partial<EventTypeDefaults> | null;
      /** One of the eight names. Absent lets the server pick the next unused one for a root. */
      colorKey?: string | null;
      /**
       * Absent means "nobody has said", which reads as the parent's answer and then `Standard`.
       * Unlike the colour, the server picks nothing on your behalf: an unranked kind is the
       * ordinary case, where an uncoloured one would leave a ragged calendar.
       */
      digestPriority?: DigestPriority | null;
    },
  ) =>
    call<EventType>(`/units/${unitId}/event-types/`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Every field optional: null means leave it alone, so one call can rename and archive. */
  update: (
    unitId: string,
    eventTypeId: string,
    input: {
      name?: string;
      description?: string | null;
      archived?: boolean;
      /** The whole suggestion set, or absent to leave it alone. Never field by field — the rules
       *  that make a set valid only exist across fields. */
      defaults?: Partial<EventTypeDefaults> | null;
      /**
       * A name to recolour, the empty string to clear it back to the parent's, absent to leave it
       * alone. The empty string carries "clear" because a JSON null is indistinguishable from an
       * absent field by the time the server has deserialised it.
       */
      colorKey?: string;
      /**
       * One of the three words to set it, the empty string to clear it back to the parent's,
       * absent to leave it alone — the same three-way convention `colorKey` uses, and for the same
       * reason.
       */
      digestPriority?: string;
    },
  ) =>
    call<EventType>(`/units/${unitId}/event-types/${eventTypeId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  /**
   * What a type refines, or null to make it a root again.
   *
   * Its own call rather than a field on `update`, because null has to mean "make this a root" and
   * a PATCH field that is absent has to mean "leave it alone" — one nullable field cannot say both.
   */
  setParent: (unitId: string, eventTypeId: string, parentEventTypeId: string | null) =>
    call<EventType>(`/units/${unitId}/event-types/${eventTypeId}/parent`, {
      method: 'PUT',
      body: JSON.stringify({ parentEventTypeId }),
    }),

  /**
   * The whole live set in the order it should read, not a move instruction. The server rewrites
   * every position in one transaction, so two types cannot end up sharing one.
   */
  reorder: (unitId: string, orderedIds: string[]) =>
    call<void>(`/units/${unitId}/event-types/order`, {
      method: 'PUT',
      body: JSON.stringify({ orderedIds }),
    }),
};
